import type { TtsVoiceStyle } from "../shared/protocol.js";

const STYLE_STORAGE_KEY = "softi-meet-tts-voice-style";

const voiceStyleFromStorage = (): TtsVoiceStyle => {
  const stored = localStorage.getItem(STYLE_STORAGE_KEY);
  return stored === "male" || stored === "female" ? stored : "auto";
};

const MALE_VOICE_PATTERN =
  /\b(male|in[\s-]?joon|hyun[\s-]?su|min[\s-]?jun|do[\s-]?yun)\b|남성|인준|현수|민준|도윤/i;
const FEMALE_VOICE_PATTERN =
  /\b(female|sun[\s-]?hi|hea[\s-]?mi|yuna|sora)\b|여성|선희|해미|유나|소라/i;

export class BrowserTtsController {
  private enabled =
    localStorage.getItem("softi-meet-tts-enabled") !== "false";
  private voiceStyle = voiceStyleFromStorage();
  private readonly pendingUtterances = new Set<SpeechSynthesisUtterance>();

  constructor(
    private readonly onPlaybackStateChange: (playing: boolean) => void = () =>
      undefined,
  ) {}

  isEnabled(): boolean {
    return this.enabled;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    localStorage.setItem("softi-meet-tts-enabled", String(enabled));
    if (!enabled) this.stop();
  }

  isPlaying(): boolean {
    return this.pendingUtterances.size > 0;
  }

  getVoiceStyle(): TtsVoiceStyle {
    return this.voiceStyle;
  }

  setVoiceStyle(style: TtsVoiceStyle): void {
    this.voiceStyle = style;
    localStorage.setItem(STYLE_STORAGE_KEY, style);
  }

  async speak(
    text: string,
    requestedStyle: TtsVoiceStyle = this.voiceStyle,
  ): Promise<boolean> {
    if (
      !this.enabled ||
      !("speechSynthesis" in window) ||
      !("SpeechSynthesisUtterance" in window)
    ) {
      return false;
    }
    const selection = await this.findLocalKoreanVoice(requestedStyle);
    if (!selection) return false;

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.voice = selection.voice;
    utterance.lang = selection.voice.lang || "ko-KR";
    utterance.rate = 0.96;
    utterance.pitch = selection.matchedStyle
      ? 1
      : requestedStyle === "male"
        ? 0.78
        : requestedStyle === "female"
          ? 1.08
          : 1;
    utterance.volume = 1;
    let finished = false;
    const finish = (): void => {
      if (finished) return;
      finished = true;
      const removed = this.pendingUtterances.delete(utterance);
      if (removed && this.pendingUtterances.size === 0) {
        this.onPlaybackStateChange(false);
      }
    };
    utterance.onend = finish;
    utterance.onerror = finish;
    this.pendingUtterances.add(utterance);
    if (this.pendingUtterances.size === 1) {
      this.onPlaybackStateChange(true);
    }
    try {
      window.speechSynthesis.speak(utterance);
    } catch {
      finish();
      return false;
    }
    return true;
  }

  stop(): void {
    window.speechSynthesis?.cancel();
    if (this.pendingUtterances.size === 0) return;
    this.pendingUtterances.clear();
    this.onPlaybackStateChange(false);
  }

  private async findLocalKoreanVoice(
    style: TtsVoiceStyle,
  ): Promise<
    { voice: SpeechSynthesisVoice; matchedStyle: boolean } | undefined
  > {
    let voices = window.speechSynthesis.getVoices();
    if (voices.length === 0) {
      voices = await new Promise<SpeechSynthesisVoice[]>((resolve) => {
        const finish = (): void => {
          window.clearTimeout(timer);
          window.speechSynthesis.removeEventListener("voiceschanged", finish);
          resolve(window.speechSynthesis.getVoices());
        };
        const timer = window.setTimeout(finish, 800);
        window.speechSynthesis.addEventListener("voiceschanged", finish, {
          once: true,
        });
      });
    }
    const koreanVoices = voices.filter(
      (voice) =>
        voice.localService && voice.lang.toLowerCase().startsWith("ko"),
    );
    if (koreanVoices.length === 0) return undefined;
    if (style === "auto") {
      return {
        voice:
          koreanVoices.find((voice) => voice.default) ??
          koreanVoices[0]!,
        matchedStyle: true,
      };
    }
    const pattern =
      style === "male" ? MALE_VOICE_PATTERN : FEMALE_VOICE_PATTERN;
    const matched = koreanVoices.find((voice) =>
      pattern.test(`${voice.name} ${voice.voiceURI}`),
    );
    return {
      voice: matched ?? koreanVoices[0]!,
      matchedStyle: Boolean(matched),
    };
  }
}
